<template>
  <EquipmentRow
    :key="`${props.stack || 'default'}-${sensor?.id || 'none'}`"
    data-cy="sensor-row"
  >
    <template #left></template>
    <template #default="{ toggle }">
      <AddRemoveButtons
        data-cy="sensor-row-add-remove-buttons"
        :hide-add-below="true"
        :disable-remove="!sensor?.id"
        @remove="
          props.stack === 'lower'
            ? equipmentForm.update({
                r_lower_sensor: null,
                lower_sensor_id: null
              } as Partial<typeof equipmentForm.data.value>)
            : equipmentForm.update({
                r_sensor: null,
                sensor_id: null
              })
        "
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            data-cy="sensor-row-equipment-panel"
            :rule-names="['sensor_obsolete']"
            @toggle="toggle"
          >
            <template #header>
              <span>Sensor</span>
            </template>
            <template v-if="sensor" #trigger>
              {{ computeSensorLabel(sensor) }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <SensorSearchView
        :is-admin="false"
        data-cy="sensor-row-sensor-search-view"
        @select="handleSensorSelect($event, toggle)"
      />
    </template>
    <template #right>
      <div class="flex">
        <div class="mr-2">
          <ConverterInput
            label="Length"
            data-cy="sensor-row-converter-input"
            :model-value="sensor?.length_inches || 0"
            :base-unit="LengthUnits.Inches"
            :target-unit="getLargeLengthUnit('Length.convert.Inches')"
            :model="LargeLengthUnits.options()"
            mode="convert"
          />
        </div>
        <div class="mr-2">
          <ConverterInput
            label="Weight"
            :model-value="sensor?.weight_pounds || 0"
            :base-unit="MassUnits.Pounds"
            :model="MassUnits.options()"
            mode="convert"
          />
        </div>
      </div>
    </template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import SensorSearchView from "./SensorSearchView.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computeSensorLabel } from "@/models/equipment/sensors/properties";
import type { Sensors } from "gql/api";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);

// Stack-aware sensor selection
const sensor = computed(() => {
  if (props.stack === "lower") {
    return equipment.value.r_lower_sensor;
  }
  return equipment.value.r_sensor;
});

provide(
  "selected-equipment-id",
  computed(() => sensor.value?.id)
);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

function handleSensorSelect(sensor: Sensors, toggle: () => void) {
  toggle();
  if (props.stack === "lower") {
    equipmentForm.update({
      r_lower_sensor: sensor,
      lower_sensor_id: sensor?.id
    } as Partial<typeof equipmentForm.data.value>);
  } else {
    equipmentForm.update({
      r_sensor: sensor,
      sensor_id: sensor?.id
    });
  }
}
</script>
